import React, { useEffect, useMemo, useState } from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { RootState } from "../../redux/store";
import AuthLayout from "../../Layout/AuthLayout";
import {
  PhoneNumberInput,
  GeneralButton,
  Loader,
  Modal,
} from "../../components";
import { useToast } from "../../Services/ToastContext";
import { setNewPhoneNumber } from "../../redux/reducers/registrationSlice";
import { useNavigate } from "react-router-dom";
import { createPhoneNumberSchema } from "../../utils/phoneValidation";
import { EndPoints } from "../../Constants/endpoints";
import { useAppDispatch, useAppSelector, usePostData } from "../../hooks";

interface AddNewPhoneNumberFormInputs {
  PhoneNumber: string;
}
const AddNewPhoneNumber: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const validate = useAppSelector((state: RootState) => state.phValidation);

  const addNewPhoneNumberSchema = useMemo(() => {
    return createPhoneNumberSchema(t, validate);
  }, [t, validate]);

  const methods = useForm<AddNewPhoneNumberFormInputs>({
    resolver: zodResolver(addNewPhoneNumberSchema),
    defaultValues: {
      PhoneNumber: "",
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    trigger,
    formState: { isSubmitted },
  } = methods;

  const changePhoneNumberMutation = usePostData(EndPoints.ChangePhoneNumber);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState("");
  const [displayPhoneNumberForPopUp, setDisplayPhoneNumberForPopUp] =
    useState("");

  const registration = useAppSelector((state: RootState) => state.registration);

  useEffect(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [t, trigger, isSubmitted]);

  const onSubmit: SubmitHandler<AddNewPhoneNumberFormInputs> = (data) => {
    setDisplayPhoneNumber(data.PhoneNumber);
    setDisplayPhoneNumberForPopUp(` ${validate.Code}  ${data.PhoneNumber} `);
    setIsModalOpen(true);
  };

  const handleConfirm = () => {
    setIsModalOpen(false);
    const id = registration.ChangePhoneNumberId;
    const countryId = Number(registration.userDetails.CountryId);

    if (id !== null && !isNaN(countryId)) {
      let processedPhoneNumber = displayPhoneNumber;
      if (processedPhoneNumber.startsWith("0")) {
        processedPhoneNumber = processedPhoneNumber.slice(1);
      }
      dispatch(setNewPhoneNumber(processedPhoneNumber));
      changePhoneNumberMutation.mutate(
        {
          Id: id,
          CountryId: countryId,
          PhoneNumber: processedPhoneNumber,
        },
        {
          onSuccess: (data) => {
            if (data.Status) {
              showToast(t("verification_code_sent_successfully"), "success");
              navigate("/send-code-change-number");
            } else {
              showToast(
                data.ErrorMessage || t("phone_number_change_failed"),
                "error",
              );
            }
          },

          onError: (error: any) => {
            const errorMessage =
              error?.response?.data?.message ||
              error?.message ||
              t("an_error_occurred_while_changing_phone_number");
            showToast(errorMessage, "error");
          },
        },
      );
    } else {
      showToast(t("user_details_missing"), "error");
    }
  };

  return (
    <FormProvider {...methods}>
      <AuthLayout
        title={t("new_number_with_icon")}
        description={t(
          "please_enter_your_phone_number_to_send_you_a_verification_code",
        )}
        lottieImage={null}
        
      >
        <form
          className="w-full"
          onSubmit={handleSubmit(onSubmit)}
          aria-labelledby="add-phone-number-title"
        >
          <h2 id="add-phone-number-title" className="sr-only">
            {t("add_new_phone_number")}
          </h2>

          <div className="-mt-2 mb-36">
            <PhoneNumberInput controllerName="PhoneNumber" />
          </div>
          <GeneralButton
            type="submit"
            title={
              changePhoneNumberMutation.isPending ? <Loader /> : t("send_code")
            }
            color="blue"
            block
            disabled={changePhoneNumberMutation.isPending}
            ariaLabel={t("send_code")}
          />
          {/* {changePhoneNumberMutation.isPending && <Loader />} */}
        </form>

        {/* Confirmation Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          aria-labelledby="confirm-phone-number-title"
          header={t("verifying_number")} 
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            aria-labelledby="confirm-phone-number-title"
          >
            <p className="text-body-text mb-4">
              {t("are_you_sure_that")}
              <strong dir="ltr" className="text-primary-text">
                {displayPhoneNumberForPopUp}
              </strong>
              {t("your_correct_number?")}
            </p>
            <div className="mt-28 flex justify-end gap-4">
              <GeneralButton
                title={t("edit")}
                color="lite"
                onClick={() => setIsModalOpen(false)}
                ariaLabel={t("edit")}
                className="!w-32"
              />
              <GeneralButton
                title={t("continue")}
                type="submit"
                ariaLabel={t("continue")}
                className="!w-60"
              />
            </div>
          </form>
        </Modal>
      </AuthLayout>
    </FormProvider>
  );
};

export default AddNewPhoneNumber;
